import {useState, useRef} from 'react'
import {Map, fromJS} from 'immutable'
import PropTypes from 'prop-types'

import ButtonGroup from '../../../../../shared_components/core/buttons/ButtonGroup'
import {Cancel, Accept} from '../../../../../dashboard/src/components/blocks/Buttons'
import Closer from '../../../../../shared_components/core/closer/Closer'
import Modals from '../../../../../shared_components/core/modals/Modals'
import ItemPermissionsSelectForm from '../../../../../shared_components/item_permissions/ItemPermissionsSelectForm'
import LinkButton from '../../../../../shared_components/mui_base_components/link_button/LinkButton'
import {ModalHeader} from '../../../../../shared_components/core/modals/ModalHeaders'
import Fields from '../../../../../shared_components/forms/v2/fields/index'
import Form from '../../../../../shared_components/forms/v2/Form'

import {capItemIdsToManageable} from '../../../../../lib/itemPermissionTools'

import './requestItemPermissionsModal.scss'

const BaseRequestItemPermissionsModal = props => {
  const {
    onConfirm,
    confirmationText,
    requireItemPermissionSelection,
    ownership,
    itemPermissionIds,
    showDefaultSettings,
    onSaveDefaultSettings
  } = props
  // The defaults (+itemPermissionIds+) come from the firm user's saved defaults or the
  // existing edit-item-ids on the firm-ownership, neither of which is recomputed when
  // the account manager tightens the firm's editable items preset. Cap the initial
  // selection (and any reset on modal toggle) to the firm's current
  // +manageable-edit-item-ids+ so a "save without touching the tree" can't ship items
  // the AM has restricted — the +ItemPermissionsSelectForm+ already hides those rows
  // visually, but their ids would otherwise stay in +checkedItemIds+ state and end up
  // on the +request-item-permissions-invite+.
  const cappedItemPermissionIds = () => capItemIdsToManageable(
    itemPermissionIds,
    ownership.get('manageable-edit-item-ids')
  )

  const [showModal, setShowModal] = useState(false)
  const [checkedItemIds, setCheckedItemIds] = useState(cappedItemPermissionIds())
  const formRef = useRef(null)

  const toggleModal = () => {
    setShowModal(!showModal)
    setCheckedItemIds(cappedItemPermissionIds())
  }

  const handleConfirm = () => {
    if (showDefaultSettings && onSaveDefaultSettings && formRef.current &&
        !formRef.current.formData().getIn(['default-item-permissions-checkbox', 'value']).isEmpty())
      onSaveDefaultSettings(checkedItemIds)


    onConfirm(checkedItemIds)
    toggleModal()
  }

  const closer = () => <Closer closer={toggleModal} />

  return (
    <span className='request-item-permissions-modal forms-playground'>
      <LinkButton className='item-permissions-modal-link' onClick={toggleModal}>
        Manage permission
      </LinkButton>
      <Modals.FullHeightModalLarge
        showModal={showModal}
        closerComponent={closer}>
        <ModalHeader
          heading='For which sections would you like permission to edit?'
          note="Note: some sections may be restricted by your firm's compliance settings."
        />
        <ItemPermissionsSelectForm
          checkedItemIds={checkedItemIds}
          onCheck={setCheckedItemIds}
          permittedItemIds={ownership.get('manageable-edit-item-ids')}
        />

        <ButtonGroup>
          <Cancel onClick={toggleModal} />
          <Accept
            onClick={handleConfirm}
            disabled={requireItemPermissionSelection && !checkedItemIds.length}>
            {confirmationText}
          </Accept>
        </ButtonGroup>
        {showDefaultSettings && (
          <Form className='core' ref={formRef}>
            <Fields.CheckboxList
              data={
                fromJS({
                  id: 'default-item-permissions-checkbox',
                  items: [{value: 'true', label: 'Save as default settings'}],
                  name: 'default-item-permissions-checkbox',
                  value: []
                })
              }
            />
          </Form>
        )}
      </Modals.FullHeightModalLarge>
    </span>
  )
}

BaseRequestItemPermissionsModal.propTypes = {
  onConfirm: PropTypes.func.isRequired,
  confirmationText: PropTypes.string,
  requireItemPermissionSelection: PropTypes.bool,
  ownership: PropTypes.instanceOf(Map).isRequired,
  itemPermissionIds: PropTypes.array,
  showDefaultSettings: PropTypes.bool,
  onSaveDefaultSettings: PropTypes.func
}

BaseRequestItemPermissionsModal.defaultProps = {
  confirmationText: 'Save',
  showDefaultSettings: false
}

export default BaseRequestItemPermissionsModal
